/**
 * This is the Central Socket Controller.
 *
 * All sockets run are set up through it and run through it.
 *
 * The main benefit is that you no longer have to worry about
 * forgetting to write a particular key line of code somewhere,
 * and you no longer have to repeat yourself (e.g. we keep it DRY)
 * across every new socket implementation.
 *
 * Likewise, if we ever want to change or improve anything or even
 * fix something, we can just come here and do it *once* and it will
 * apply across all our socket implementations and we don't need to
 * worry about keeping all sockets individually in check and having
 * to re-write the same aspects across multiples files.
 *
 * As a consequence, there's less work and everything sockets is
 * less prone to errors.
 *
 * Currently, this prevents us from having to write the same code
 * to check if the socket is connected or not, attempts to reconnect,
 * waiting for the connection to get established or throw an error,
 * turning off all socket event listeners and also re-initializing
 * all event listeners during a reboot of any given socket. This
 * also centralizes error logging across all sockets.
 *
 * Additionally, this make *any* socket, available for re-use from
 * anywhere within the app, by simply referring to it. It acts like
 * a central socket data store. Each socket namespace can be easily
 * accessed by using its namespace.
 *
 * All even listeners of each individual socket are registered with
 * this Central Socket Controller and are managed through it, each
 * having its respective callbacks associated.
 *
 * It's designed in such a way, that minimal setup is needed per
 * socket and all necessary functions will be automatically called
 * upon initialization of the Socket namespace.
 *
 */

import io from "socket.io-client";
import store from "../store";
import { BASE_PATH } from "../constants";
import { constructAndLogError } from "../utilities/errorLogger";

/* Container of all sockets, their respective event listeners and callbacks. */
const sockets = {};

class SocketNSP {
  /**
   * Sets up the initial container for the specified socket namespace.
   * @param {String} NSP Socket namespace name
   */
  constructor(NSP, customListeners = {}) {
    this.NSP = NSP ? NSP : ""; // Socket namespace
    this.customListeners = customListeners; // Used for saving custom event listeners of any socket for re-use
    this.socket = null;
    this.loaded = false;
  }

  init(UID) {
    /** Initialize authentication config. */
    const authConfig = { withCredentials: true, query: { UID } };

    /** Initialize socket namespace and store a reference to it. */
    this.socket = io(BASE_PATH + this.NSP, authConfig);

    /** Initialize all socket event listeners */
    this.initSocketListeners();
  }

  initSocketListeners() {
    /* Initialize any existing custom listeners */
    for (let event in this.customListeners) {
      const callback = this.customListeners[event];
      this.addListener(event, callback);
    }

    /* Initialize default listeners */
    this.initDefaultListeners();

    /** Set socket as loaded */
    this.loaded = true;
  }

  /** Initializes standard socket listeners if they have not been overwritten. */
  initDefaultListeners() {
    /* Default events for the namespace with respective callbacks */
    const defaultEvents = {
      disconnect: this.handleDisconnect,
      connect_error: this.handleConnectError,
    };

    /** Initialize default events with their callbacks */
    for (let event in defaultEvents) {
      const existingCallbacks = this.socket._callbacks;
      /* If no custom event has been set for the default event, initialize event */
      if (existingCallbacks["$" + event] === undefined) {
        const callback = defaultEvents[event];
        this.addListener(event, callback);
      }
    }
  }

  /**
   * Used to add a single listener to a socket
   * @param {String} event Event to add to socket
   * @param {Function} callback Callback to run upon added event happening
   */
  addListener(event, callback) {
    /* Ensure event is a String and callback is a Function */
    if (typeof event === "string" && typeof callback === "function") {
      this.socket.on(event, callback);
    } else {
      throw new Error("Event or callback is of wrong data Type");
    }
  }

  /**
   * General purpose emit function that checks and ensures that
   * socket exists and is connected and user is authenticated
   * before attempting to emit, and then makes the emit.
   *
   * @param {String} event Event name to emit
   * @param {Object} data Any data to pass with the data
   * @returns {Object} Return result of emit
   */
  async emit(event, data = {}) {
    try {
      /* Ensure no emits before socket is ready. */
      if (!this.loaded) return;

      /** Ensure socket is connected before attempting to emit. */
      if (!this.isConnected()) {
        await this.attemptReconnectSocket();
      }

      return await new Promise((resolve) => {
        this.socket.emit(event, data, (result) => {
          console.log("THE RESULT", event, result);
          if (event === "CANCEL_ONE_SLOT") resolve(result.success);
          else resolve(result);
        });
      });
    } catch (error) {
      console.error("Error during emit:", error);
      throw error;
    }
  }

  /**
   * Returns state of whether a socket of a specified namespace is connected.
   * @returns {Boolean} Whether socket exists and is connected
   */
  isConnected() {
    return this.socket && this.socket.connected;
  }

  /** Attempts reconnect an existing socket that is not connected. */
  async attemptReconnectSocket() {
    console.log("ATTEMPTING TO RECONNECT!");
    try {
      /* If no socket and no namespace, something has gone totally wrong. */
      if (!this.socket && !this.NSP) throw new Error("Namespace missing for socket that should exist.");

      /* If no socket but the namespace exists, ensures socket namespace is initialized. */
      if (!this.socket && this.NSP) await initSocketNSP(this.NSP);

      /** If socket namespace is loaded but socket itself not found at all, reboot socket */
      if (!this.socket && this.loaded) await this.rebootSocket();

      /* If socket exists but is simply not connected, attempt re-connect. */
      if (this.socket && !this.socket.connected) await this.socket.connect();

      /* Before moving on, check that socket is on. */
      if (!this.socket.connected) await this.waitForOpenConnection();
    } catch (error) {
      await constructAndLogError(error);
    }
  }

  /** Removes all event listeners from the namespace's socket. */
  removeEventListeners() {
    this.socket.off();
  }

  /** When user leaves the page and socket is still running */
  disconnectSocket() {
    if (this.socket) this.socket.disconnect();
  }

  /** Default disconnect handler. Can be overwritten. */
  handleDisconnect(reason) {
    console.log(`${this.NSP} socket disconnected with reason: `, reason);
  }

  /** Default connection error handler. Can be overwritten. */
  handleConnectError(err) {
    /* We are unable to connect you to the chat, but we've logged the error. Thank you for the patience! */
    console.log(`Connection error @${this.NSP} socket.`, err);
  }

  async rebootSocket() {
    if (this.socket || this.socket.connected) {
      this.removeEventListeners();
      this.disconnectSocket();
    }

    /** Check that user is authenticated. If not wait and try again. */
    const UID = await ensureAuthenticatedAndReturnUID();
    this.init(UID);
  }

  async waitForOpenConnection() {
    let timeout = null;
    let intervalTimeMS = 200;
    let allowedTimeMS = 0;
    let attemptNr = 0;
    let maxAttempts = 50;

    let self = this;

    async function executeIfAllowed() {
      /** If max attempts reached, error out (could in theory attempt full reboot to socket) */
      if (attemptNr >= maxAttempts) throw new Error(`Maximum number of attempts exceeded while trying to connect to socket: ${self.NSP}`);

      /* If allowed time has not been set (first round) OR allowed time has arrived, check for connection status */
      if (Date.now() >= allowedTimeMS) {
        if (!self.socket.connected) {
          /* Attempt connect the socket */
          await self.socket.connect();

          /* If still not connected, wait and try again*/
          if (!self.socket.connected) {
            attemptNr++;
            allowedTimeMS = Date.now() + intervalTimeMS;
            timeout = setTimeout(executeIfAllowed, intervalTimeMS);
          }
        }
      } else {
        /* If allowed time has not yet arrived, set another timeout */
        console.log("Not yet allowed, setting new timeout...");
        timeout = setTimeout(executeIfAllowed, intervalTimeMS);
      }
    }

    /* If socket is connected, exit */
    if (this.socket && this.socket.connected) {
      /* If a timeout exists, clear it before exiting */
      if (timeout) clearTimeout(timeout);
      return;
    }

    /* Begin socket re-connecting loop until connected or max attempts reached. */
    await executeIfAllowed();
  }
}

/**
 * ALL SOCKET NAMESPACE ARE INITIALIZED THROUGH THIS FUNCTION.
 *
 * @NB_IMPORTANT !!! Function must be below the class due to the way classes are hoisted.
 * https://developer.mozilla.org/en-US/docs/Glossary/Hoisting
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Classes
 * @param {String} NSP Name of the socket namespace to initialize
 */
export async function initSocketNSP(NSP, customListeners) {
  try {
    /** Ensure NSP is provided */
    if (!NSP) {
      console.log("No namespace provided for socket initialization.");
      return;
    }

    /** Check that user is authenticated. If not wait and try again. */
    const UID = await ensureAuthenticatedAndReturnUID();

    /** If the socket has not been initialized yet, initialize it. */
    if (!sockets[NSP]) {
      /** Create a socket namespace data reference. */
      sockets[NSP] = new SocketNSP(NSP, customListeners);

      /** Initialize the socket namespace. */
      sockets[NSP].init(UID);
    }

    return sockets[NSP];
  } catch (error) {
    await constructAndLogError(error);
  }
}

async function ensureAuthenticatedAndReturnUID() {
  try {
    const UID = store && store.state && store.state.auth && store.state.auth.user && store.state.auth.user._id ? store.state.auth.user._id : null;
    if (!UID) throw new Error("User not authenticated to join socket!");
    return UID;
  } catch (error) {
    await constructAndLogError(error);
  }
}

export default {
  initSocketNSP,
};
